/**
 * Règles bâties sur le schéma des sections communes — voir besoin-commun-schema.js
 * pour le contexte général (les deux moteurs de rendu qui le partagent).
 *
 * Ce module porte la validation des sections communes et le report des
 * réponses de l'étape 1 (analyse du besoin) vers l'étape 2 (questionnaire
 * d'organisation). Le schéma et les helpers d'affichage vivent dans
 * besoin-commun-sections.js.
 */
import { BESOIN_COMMUN_FIELDS, BESOIN_COMMUN_KEYS, BESOIN_COMMUN_ANCIENNES_CLES, isFieldVisible, isAutreVisible, isFieldRequired } from './besoin-commun-sections.js'

/**
 * Une question dont la valeur par défaut est déjà une réponse (« Non », false…)
 * ne peut jamais manquer : l'astérisque la marque obligatoire à l'écran, mais
 * seule l'absence de réponse possible bloque la validation.
 */
function blocksValidation(field, values) {
  if (!isFieldRequired(field, values)) return false
  return isEmptyValue(field.default ? field.default() : '')
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'string') return value.trim() === ''
  return false
}

/**
 * Valide les sections communes.
 * @returns {{ valid: boolean, errors: Record<string,string>, missing: string[] }}
 *   `errors` alimente l'affichage champ par champ du questionnaire d'organisation,
 *   `missing` la liste « il manque… » du point de vigilance de l'analyse du besoin.
 */
export function validateBesoinCommun(data) {
  const values = data || {}
  const errors = {}
  const missing = []

  for (const field of BESOIN_COMMUN_FIELDS) {
    if (!isFieldVisible(field, values)) continue

    if (blocksValidation(field, values) && isEmptyValue(values[field.key])) {
      errors[field.key] = field.requiredMessage || `Le champ « ${field.label} » est requis.`
      missing.push(field.missingLabel || field.label)
    }

    if (field.autreKey && field.autreRequired && isAutreVisible(field, values) && isEmptyValue(values[field.autreKey])) {
      errors[field.autreKey] = field.autreRequiredMessage || 'Veuillez préciser.'
      missing.push(field.autreLabel || `${field.label} — précisez`)
    }
  }

  return { valid: missing.length === 0, errors, missing }
}

/** Extrait des sections communes d'un objet plus large (analyse du besoin, partie bleue…). */
export function pickBesoinCommun(source) {
  if (!source || typeof source !== 'object') return null
  const picked = {}
  for (const key of [...BESOIN_COMMUN_KEYS, ...BESOIN_COMMUN_ANCIENNES_CLES]) {
    if (Object.prototype.hasOwnProperty.call(source, key)) picked[key] = source[key]
  }
  return Object.keys(picked).length > 0 ? picked : null
}

/**
 * Reporte les réponses de l'étape 1 sur l'étape 2.
 *
 * `saisies` est ce que l'organisateur a déjà enregistré : une clé qui y figure
 * lui appartient et n'est jamais écrasée. Tant qu'il n'a rien enregistré, la
 * dernière version de l'analyse du besoin est reprise — y compris les
 * corrections faites par le commercial après l'envoi du questionnaire.
 *
 * @param {object} target - la partie bleue à préremplir (mutée)
 * @param {object|null} besoin - les réponses de l'analyse du besoin
 * @param {object} saisies - les données déjà enregistrées côté étape 2
 */
export function applyBesoinCommunPrefill(target, besoin, saisies = {}) {
  if (!target || !besoin || typeof besoin !== 'object') return target
  const dejaRepondu = saisies && typeof saisies === 'object' ? saisies : {}

  for (const key of [...BESOIN_COMMUN_KEYS, ...BESOIN_COMMUN_ANCIENNES_CLES]) {
    if (Object.prototype.hasOwnProperty.call(dejaRepondu, key)) continue
    const value = besoin[key]
    if (value === undefined || value === null) continue
    target[key] = Array.isArray(value) ? [...value] : value
  }
  return target
}
